// Access (/internal/access, VRG-727) — the primary "Invite" form. One form for all three
// kinds of access; each ticked box runs that access's existing mechanism. No
// email goes out unless one of the "Send … email" boxes is ticked.
import React, { useEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { Button, FieldLabel, Input, Modal, Switch } from '@repo/ui'

import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import { usePeopleActions, type GrantResult } from 'hooks/api/usePeople'

import { DEFAULT_EXPIRY, ExpiryField, Option, resolveExpiry, type ExpiryValue } from './dialogs'

type State = {
  name: string
  email: string
  linkOnly: boolean
  website: boolean
  expiry: ExpiryValue
  pricing: boolean
  pricingEmail: boolean
  platform: boolean
  platformEmail: boolean
  note: string
}

const INITIAL: State = {
  name: '',
  email: '',
  linkOnly: false,
  website: true,
  expiry: DEFAULT_EXPIRY,
  pricing: false,
  pricingEmail: false,
  platform: false,
  platformEmail: false,
  note: '',
}

// House components only (§28/§31): `@repo/ui` Modal / Input / FieldLabel / Switch / Button,
// the platform `SelectField` + `DatePickerField` via ExpiryField, and `CheckboxRow` for
// the options. Every label is FieldLabel's body3 with its 8px gap; MUI is layout only.

export const InvitePersonModal = ({
  open,
  onClose,
  onDone,
}: {
  open: boolean
  onClose: () => void
  onDone: (result: GrantResult, name: string) => void
}) => {
  const { open: notification } = useSnackbar()
  const { invite } = usePeopleActions()
  const [s, setS] = useState<State>(INITIAL)
  const [errors, setErrors] = useState<Partial<Record<'name' | 'email' | 'expiry' | 'access', string>>>({})
  const set = (patch: Partial<State>) => setS((prev) => ({ ...prev, ...patch }))

  useEffect(() => {
    if (open) {
      setS(INITIAL)
      setErrors({})
    }
  }, [open])

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault()
    const next: typeof errors = {}
    if (!s.name.trim()) next.name = 'Name is required'
    if (!s.linkOnly && !/^\S+@\S+\.\S+$/.test(s.email.trim())) next.email = 'Enter an email, or turn on "Link only"'
    const expiry = s.website ? resolveExpiry(s.expiry) : null
    if (expiry && 'error' in expiry) next.expiry = expiry.error
    if (!s.website && !s.pricing && !s.platform) next.access = 'Choose at least one kind of access'
    setErrors(next)
    if (Object.keys(next).length) return

    invite.mutate(
      {
        name: s.name.trim(),
        email: s.linkOnly ? undefined : s.email.trim(),
        link_only: s.linkOnly,
        website: { enabled: s.website, expires_at: expiry && 'iso' in expiry ? expiry.iso : undefined },
        pricing: { enabled: !s.linkOnly && s.pricing, send_email: s.pricingEmail },
        platform: { enabled: !s.linkOnly && s.platform, send_email: s.platformEmail },
        note: s.note.trim() || undefined,
      },
      {
        onSuccess: (result) => onDone(result, s.name.trim()),
        onError: (error) => notification({ type: 'error', title: getErrorMessage(error) }),
      },
    )
  }

  return (
    <Modal
      title="Invite"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button onClick={() => submit()} loading={invite.isPending}>
            Invite
          </Button>
        </>
      }
    >
      <Stack component="form" onSubmit={submit} spacing={4} mt={2}>
        <Input
          label="Name"
          value={s.name}
          onValueChange={(v: string) => set({ name: v })}
          placeholder="Jane Investor"
          error={Boolean(errors.name)}
          helperText={errors.name}
        />

        <Stack>
          <FieldLabel
            label="Email"
            action={
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body4" color="text.secondary">
                  Link only
                </Typography>
                <Switch
                  checked={s.linkOnly}
                  inputProps={{ 'aria-label': 'Link only (no email)' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    set({ linkOnly: e.target.checked, ...(e.target.checked ? { website: true, pricing: false, platform: false } : {}) })
                  }
                />
              </Stack>
            }
          />
          {s.linkOnly ? (
            <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              No email: a website link only, listed under this name. Pricing and platform access need an email.
            </Typography>
          ) : (
            <Input
              value={s.email}
              type="email"
              onValueChange={(v: string) => set({ email: v })}
              placeholder="jane@fund.com"
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          )}
        </Stack>

        <Stack spacing={3}>
          <FieldLabel label="Access" />
          <Option
            name="access-website"
            label="Website"
            hint="A personal link past the vera.inc password screen."
            checked={s.website}
            disabled={s.linkOnly}
            onChange={(v) => set({ website: v })}
          />
          {s.website && (
            <Stack sx={{ pl: 6 }}>
              <ExpiryField label="Expires" value={s.expiry} onChange={(v) => set({ expiry: v })} error={errors.expiry} />
            </Stack>
          )}
          <Option
            name="access-pricing"
            label="Pricing"
            hint="The real price list on vera.inc/pricing."
            checked={s.pricing}
            disabled={s.linkOnly}
            onChange={(v) => set({ pricing: v, pricingEmail: v ? s.pricingEmail : false })}
          />
          {s.pricing && (
            <Option name="send-pricing-email" label="Send pricing email" checked={s.pricingEmail} onChange={(v) => set({ pricingEmail: v })} indent />
          )}
          <Option
            name="access-platform"
            label="Platform"
            hint="Allowed to create a Vera account."
            checked={s.platform}
            disabled={s.linkOnly}
            onChange={(v) => set({ platform: v, platformEmail: v ? s.platformEmail : false })}
          />
          {s.platform && (
            <Option name="send-platform-email" label="Send invite email" checked={s.platformEmail} onChange={(v) => set({ platformEmail: v })} indent />
          )}
          {errors.access && (
            <Typography variant="body4" color="error">
              {errors.access}
            </Typography>
          )}
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Nothing is emailed unless you tick a &ldquo;Send&rdquo; box. You&apos;ll get every link to copy.
          </Typography>
        </Stack>

        <Input
          label="Note"
          labelAdornment="(optional, staff only)"
          value={s.note}
          onValueChange={(v: string) => set({ note: v })}
          placeholder="Seed round, met at the Paris show"
        />
      </Stack>
    </Modal>
  )
}
